import { parse_b64_or_array_like_data_attribute } from './index';

export const ELEMENT_REFCODES_ATTRIBUTE = 'data-hub-refcodes';
export const ELEMENT_REFCODES_ATTRIBUTE_ALIASES = Object.freeze([
    ELEMENT_REFCODES_ATTRIBUTE,
    'data-hub-actblue-refcodes',
    // Compatibility for pages published before component refcodes stopped being
    // merged globally. It is now interpreted only along the clicked element path.
    'data-hub-page-refcodes',
]);

const try_to_fix_malformed_refcodes_json = (json_string) => {
    try {
        return JSON.parse(json_string);
    } catch (_error) {
        let fixed_string = json_string;
        fixed_string = fixed_string.replace(/^\{\{/, '{');
        fixed_string = fixed_string.replace(/:\s*'([^']*)'/g, ': "$1"');
        fixed_string = fixed_string.replace(/([a-zA-Z_][a-zA-Z0-9_]*)\s*"([^"]*)"\s*([^",}\s][^,}]*)/g, '"$1": "$3"');
        fixed_string = fixed_string.replace(/([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '"$1":');
        fixed_string = fixed_string.replace(/:\s*([^",}\s][^,}]*)/g, ': "$1"');
        fixed_string = fixed_string.replace(/"\s*}/g, '"}');
        fixed_string = fixed_string.replace(/"\s*"/g, '", "');
        fixed_string = fixed_string.replace(/([a-zA-Z_][a-zA-Z0-9_]*)\s+([^",}\s][^,}]*)/g, '"$1": "$2"');
        if (!fixed_string.endsWith('}')) {
            fixed_string += '}';
        }
        try {
            return JSON.parse(fixed_string);
        } catch (_fixed_error) {
            return null;
        }
    }
};

export const normalize_refcodes_from_object = (
    refcodes,
    error_function = typeof window !== 'undefined' ? window?.data_hub?.error_function : null
) => {
    if (!refcodes || typeof refcodes !== 'object') {
        if (typeof refcodes === 'string') {
            try {
                refcodes = JSON.parse(refcodes);
                if (typeof refcodes !== 'object' || refcodes === null || Array.isArray(refcodes)) {
                    error_function?.('Refcodes string did not contain a JSON object', null);
                    return {};
                }
            } catch (parse_error) {
                refcodes = try_to_fix_malformed_refcodes_json(refcodes);
                if (!refcodes || typeof refcodes !== 'object' || Array.isArray(refcodes)) {
                    error_function?.(`Failed to parse refcodes JSON object: ${parse_error.message}`, null);
                    return {};
                }
            }
        } else {
            if (refcodes != null) {
                error_function?.(`Refcodes must be an object or JSON string, received ${typeof refcodes}`, null);
            }
            return {};
        }
    }

    if (Array.isArray(refcodes)) {
        error_function?.('Refcodes must be an object, not an array', null);
        return {};
    }

    const normalized_refcodes = {};
    for (let [key, value] of Object.entries(refcodes)) {
        try {
            key = decodeURIComponent(key.trim().replace(/^["']|["']$/g, '').replace(/^\{?"?/, '').replace(/"?$/, ''));
            let decoded_value = value;
            if (typeof value === 'string') {
                decoded_value = decodeURIComponent(
                    value.trim().replace(/^["']|["']$/g, '').replace(/"?\}$/, '')
                );
            }

            if (key.startsWith('refcode')) {
                const suffix = key.startsWith('refcode_') ? key.slice(8) : key.slice(7);
                normalized_refcodes[`refcode${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`] = decoded_value;
                continue;
            }
            if (!/^[A-Za-z]/.test(key)) {
                error_function?.(`Refcode key must start with a letter: ${key}`, null);
                continue;
            }
            normalized_refcodes[`refcode${key.charAt(0).toUpperCase()}${key.slice(1)}`] = decoded_value;
        } catch (error) {
            error_function?.(`Error normalizing refcode ${key}: ${error}`, null);
        }
    }
    return normalized_refcodes;
};

export function parse_refcodes_attribute(raw_value, error_function = null) {
    if (raw_value == null || (typeof raw_value === 'string' && raw_value.trim() === '')) {
        return {};
    }
    return normalize_refcodes_from_object(
        parse_b64_or_array_like_data_attribute(raw_value) || raw_value,
        error_function
    );
}

export function read_element_refcodes(element, error_function = null) {
    if (!element || typeof element.getAttribute !== 'function') {
        return {};
    }
    for (const attribute_name of ELEMENT_REFCODES_ATTRIBUTE_ALIASES) {
        const raw_value = element.getAttribute(attribute_name);
        if (raw_value) {
            return parse_refcodes_attribute(raw_value, error_function);
        }
    }
    return {};
}

function build_event_element_path(dom_event, target_element = null) {
    if (typeof dom_event?.composedPath === 'function') {
        const composed_path = dom_event.composedPath();
        if (Array.isArray(composed_path) && composed_path.length > 0) {
            return composed_path.filter((node) => node && node.nodeType === Node.ELEMENT_NODE);
        }
    }
    const path = [];
    let current_element = target_element || dom_event?.target || null;
    while (current_element && current_element.nodeType === Node.ELEMENT_NODE && path.length < 16) {
        path.push(current_element);
        if (current_element.parentElement) {
            current_element = current_element.parentElement;
            continue;
        }
        current_element = current_element.getRootNode?.()?.host || null;
    }
    return path;
}

export function resolve_local_refcodes_from_event(dom_event, target_element = null, error_function = null) {
    for (const element of build_event_element_path(dom_event, target_element)) {
        const refcodes = read_element_refcodes(element, error_function);
        if (Object.keys(refcodes).length > 0) {
            return refcodes;
        }
    }
    return {};
}

export function merge_refcode_objects(...refcode_objects) {
    return refcode_objects.reduce((merged, refcodes) => ({
        ...merged,
        ...normalize_refcodes_from_object(refcodes || {}),
    }), {});
}

export function build_action_refcodes(data_hub_instance, local_refcodes = {}) {
    return merge_refcode_objects(
        data_hub_instance?.page_metadata?.refcodes || {},
        local_refcodes
    );
}

export function set_element_refcodes_attribute(element, refcodes) {
    if (!element?.setAttribute) return;
    const normalized_refcodes = normalize_refcodes_from_object(refcodes || {});
    if (Object.keys(normalized_refcodes).length === 0) {
        element.removeAttribute?.(ELEMENT_REFCODES_ATTRIBUTE);
        return;
    }
    element.setAttribute(ELEMENT_REFCODES_ATTRIBUTE, JSON.stringify(normalized_refcodes));
}
